import { Action } from "@core-services/data-types"
import { AppAbility, RouterOutputs, S3Config, UpdateOrganization } from "@core-services/portal"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button, FormControlLabel, TextField } from "@vindral/components"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { checkIfSuperUser, organizationSubjectInOrganization } from "../../../acl"
import { FormSection } from "../../../atoms/FormSection"
import { SaveButton } from "../../../atoms/SaveButton"
import { SaveSection } from "../../../atoms/SaveSection"
import { Message, MessageSection } from "../../../molecules/Message"
import { RouteBlockDialog } from "../../../molecules/RouteBlockDialog"

type OrganizationFindOne = RouterOutputs["organization"]["findOne"]

const resolver = zodResolver(UpdateOrganization)

export interface OrganizationEditS3ConfigProps {
  organization: OrganizationFindOne
  ability: AppAbility
  onUpdate: (organization: UpdateOrganization) => Promise<void>
  testCredentials: (s3Config: S3Config) => Promise<void>
}
export function OrganizationEditS3Config({
  organization,
  ability,
  onUpdate,
  testCredentials,
}: OrganizationEditS3ConfigProps) {
  const { s3Config } = organization
  const subject = organizationSubjectInOrganization(organization.publicId)
  const isSuperUser = checkIfSuperUser({ ability })
  const {
    register,
    reset,
    handleSubmit,
    getValues,
    formState: { errors, isDirty, isValid, isSubmitting, isSubmitSuccessful },
  } = useForm<UpdateOrganization>({
    resolver,
    mode: "onChange",
    defaultValues: {
      publicId: organization.publicId,
      s3Config: organization.s3Config
        ? organization.s3Config
        : {
            accessKeyId: "",
            endpoint: "",
            region: "",
            location: "",
            secretAccessKey: "",
          },
    },
  })

  useEffect(() => {
    reset({
      publicId: organization.publicId,
      name: organization.name,
      type: organization.type,
      billingStatus: organization.billingStatus,
      apiAccessEnabled: organization.apiAccessEnabled,
      recordingEnabled: organization.recordingEnabled,
      moqEnabled: organization.moqEnabled,
      suspended: organization.suspended,
      defaultSinkType: organization.defaultSinkType,
      defaultSourceType: organization.defaultSourceType,
    })
  }, [reset, organization])

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({}, { keepValues: true })
    }
  }, [isSubmitSuccessful, reset])

  const disableOverride = async () => {
    await onUpdate({ publicId: organization.publicId, s3Config: null })
    setOverrideEnabled(false)
  }

  const [overrideEnabled, setOverrideEnabled] = useState(!!s3Config)

  if (!isSuperUser) {
    return <></>
  }

  const nonOverrideDisplay = () => {
    return (
      <p className="mt-4 py-1 text-fg-subtle last-of-type:pb-3">
        <Button variant={"primary"} onClick={() => setOverrideEnabled(true)}>
          Enable Override
        </Button>
      </p>
    )
  }

  const overRideDisplay = () => {
    const columnWidth = "w-full max-w-lg"
    return (
      <div className="py-4">
        <RouteBlockDialog condition={isDirty && !isSubmitting} />

        <form onSubmit={handleSubmit(onUpdate)} className="flex flex-col gap-4">
          <FormSection
            width={columnWidth}
            title="Endpoint"
            description="The URL of your S3-compatible storage service (e.g., https://s3.eu-north-1.amazonaws.com)"
          >
            <TextField
              {...register("s3Config.endpoint")}
              error={!!errors?.s3Config?.endpoint}
              disabled={ability.cannot(Action.Update, subject, "s3Config")}
            />
            <FormControlLabel error={errors.s3Config?.endpoint?.message} />
          </FormSection>

          <FormSection
            width={columnWidth}
            title="Region"
            description="The region to route API requests to. (e.g., eu-north-1)"
          >
            <TextField
              {...register("s3Config.region")}
              error={!!errors?.s3Config?.region}
              disabled={ability.cannot(Action.Update, subject, "s3Config")}
            />
            <FormControlLabel error={errors.s3Config?.region?.message} />
          </FormSection>

          <FormSection
            width={columnWidth}
            title="Bucket Location"
            description="The region to where you want your S3 bucket to be located (e.g., eu-north-1)"
          >
            <TextField
              {...register("s3Config.location")}
              error={!!errors?.s3Config?.location}
              disabled={ability.cannot(Action.Update, subject, "s3Config")}
            />
            <FormControlLabel error={errors.s3Config?.location?.message} />
          </FormSection>

          <FormSection
            width={columnWidth}
            title="Access Key Id"
            description="Your S3 access key ID. This acts like a username and is used in combination with your secret key to authenticate requests."
          >
            <TextField
              {...register("s3Config.accessKeyId")}
              error={!!errors?.s3Config?.accessKeyId}
              disabled={ability.cannot(Action.Update, subject, "s3Config")}
            />
            <FormControlLabel error={errors.s3Config?.accessKeyId?.message} />
          </FormSection>

          <FormSection
            width={columnWidth}
            title="Secret Access Key"
            description="Your S3 secret access key. This acts like a password and should be kept confidential. It works with the access key ID to sign API requests."
          >
            <TextField
              {...register("s3Config.secretAccessKey")}
              error={!!errors?.s3Config?.secretAccessKey}
              disabled={ability.cannot(Action.Update, subject, "s3Config")}
            />
            <FormControlLabel error={errors.s3Config?.secretAccessKey?.message} />
          </FormSection>

          {ability.can(Action.Update, subject) && (
            <SaveSection width={columnWidth}>
              <SaveButton text="Save" disabled={!isDirty || !isValid} />
              <div className="ml-2 inline">
                <Button
                  disabled={!isValid}
                  variant={"secondary"}
                  onClick={async () => {
                    const data = getValues()
                    const s3Config = data.s3Config
                    if (!s3Config) {
                      return
                    }

                    await testCredentials(s3Config)
                  }}
                >
                  Test Credentials
                </Button>
              </div>
              <div className="ml-2 inline">
                <Button variant={"danger"} onClick={() => disableOverride()}>
                  Disable Override
                </Button>
              </div>
            </SaveSection>
          )}
        </form>
      </div>
    )
  }

  return (
    <div className="max-w-screen-xl space-y-4 py-4">
      <Message severity="warning">
        <MessageSection>
          <div className="flex w-full">
            <div className="grow">
              <span>
                Enabling S3 override may prevent channels with recording enabled from working properly, as their buckets
                are not created automatically. To resolve this, disable and then re-enable recording for each channel to
                ensure the necessary bucket is created.
              </span>
            </div>
          </div>
        </MessageSection>
      </Message>
      <div className="mb-8 flex flex-col">
        <h1 className="mb-1 text-xl font-medium">S3 Config</h1>
        <div className="text-gray-600 dark:text-gray-300">
          Override the default S3 configuration for this organization. This allows you to store recordings using your
          own S3 account.
        </div>
        {!overrideEnabled ? nonOverrideDisplay() : overRideDisplay()}
      </div>
    </div>
  )
}
